import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useQueues } from "@/hooks/useQueues";

interface WorkspaceSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorkspaceSelectionDialog({ open, onOpenChange }: WorkspaceSelectionDialogProps) {
  const navigate = useNavigate();
  const { data: organizations, isLoading } = useOrganizations();
  
  const handleWorkspaceSelect = (organizationId: string) => {
    // Navigate to the organization's queues page (which will show the default queue)
    navigate(`/dashboard/org/${organizationId}/queues`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Select Workspace
          </DialogTitle>
          <DialogDescription>
            Choose a workspace to navigate to its default queue
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[300px] pr-4">
          <div className="space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : organizations?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No workspaces found</p>
              </div>
            ) : (
              organizations?.map((organization) => (
                <Button
                  key={organization.id}
                  variant="ghost"
                  className="w-full justify-between h-auto p-4 border hover:border-primary/20"
                  onClick={() => handleWorkspaceSelect(organization.id)}
                >
                  <div className="flex items-center gap-3 text-left">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      {organization.picture_url ? (
                        <img 
                          src={organization.picture_url} 
                          alt={organization.name}
                          className="w-8 h-8 rounded-md object-cover"
                        />
                      ) : (
                        <Building2 className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium">{organization.name}</div>
                      <div className="text-sm text-muted-foreground">
                        <Badge variant="secondary" className="text-xs">
                          {organization.user_roles[0]?.role}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Button>
              ))
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}